import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { ManageTagsDialog } from '../../dialogs/manage-tags-dialog/manage-tags-dialog.component';
import { DocumentService, UserService } from '@app/shared/services';
import { ToastrService } from 'ngx-toastr';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'template-document-tag',
  templateUrl: './template-document-tag.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateDocumentTagComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() key;
  @Input() files;
  @Input() currentFormId;
  @Input() currentFolderId;
  @Input() isRemoveEditSaveSharing = false;
  @Output() attachmentTagsEvent = new EventEmitter<any>();
  @Output() tagSelectionChange = new EventEmitter<{
    type: 'document_type' | 'tag_ai_suggestion';
    tag: string;
    index: number;
    isChecked: boolean;
  }>();
  attachmentTags = {
    document_type: [],
    document_suggestion_tag: [],
  };

  errorMessage = '';
  screenWidth: number;
  newlyCreatedTag = '';
  showTagContent = false;
  filteredDocumentTypes = [];
  loadingTags = new Set<string>();
  subscription = new Subscription();
  isIpadOrTabletScreenWidth = signal(false);
  tagSelectionState: { [key: string]: boolean } = {};

  constructor(
    private dialog: MatDialog,
    private _userService: UserService,
    private documentService: DocumentService,
    private toast: ToastrService,
    private _cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getUserTags();
  }

  ngAfterViewInit(): void {
    if (this.files[0].document_type) {
      this.attachmentTags.document_type = this.files[0].document_type.split(',').filter((tag) => tag.trim());
    }
    if (this.files[0].document_suggestion_tag) {
      this.attachmentTags.document_suggestion_tag = this.files[0].document_suggestion_tag.split(',').filter((tag) => tag.trim());
    }
    this.showTagContent = true;
    this._cd.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['files']) {
      const file = changes['files'].currentValue[0];
      this.attachmentTags.document_type = file.document_type ? file.document_type.split(',').filter((tag) => tag.trim()) : [];
      this.attachmentTags.document_suggestion_tag = file.document_suggestion_tag
        ? file.document_suggestion_tag.split(',').filter((tag) => tag.trim())
        : [];
      if (!this.showTagContent) {
        this.showTagContent = true;
      }
      this._cd.detectChanges();
    }
  }

  getUserTags() {
    this.subscription.add(
      this._userService.getUserTags().subscribe(
        (res) => {
          const allUserSystemTags = res.map(({ id, name, search_terms, tag_type }) => ({ id, name, search_terms, tag_type }));
          this.filteredDocumentTypes = [...new Set([...this.filteredDocumentTypes, ...allUserSystemTags])].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          this._cd.detectChanges();
        },
        (error) => {
          this.toast.error('Error occurred while getting a list of tags', error.error.message);
        }
      )
    );
  }

  addUserTags(tag: string, skipDuplicationError = false, isSuggestionTag = false) {
    if (this.loadingTags.has(tag)) {
      return;
    }
    this.loadingTags.add(tag);

    const tagData = {
      name: tag,
      tag_type: 'user',
    };
    this.subscription.add(
      this._userService.addUserTags(tagData, loaderTypes.none).subscribe(
        (data: any) => {
          if (data?.isDuplicate && !skipDuplicationError) {
            this.errorMessage = data?.message;
            this.loadingTags.delete(tag);
            this._cd.detectChanges();
          } else if (data) {
            this.createVaultUserTag(tag, isSuggestionTag);
          }
        },
        (error: any) => {
          this.loadingTags.delete(tag);
          this.toast.error('Error creating tag', error?.error?.message || 'An error occurred');
          this._cd.detectChanges();
        }
      )
    );
  }

  createVaultUserTag(tag: string, isSuggestionTag = false) {
    const existingTags = this.attachmentTags.document_type.filter((t) => t.trim());
    const newTags = [...existingTags, tag];
    const documentTypeValue = newTags.join(',');
    const payload = {
      vault_user_form_uploads_id: this.files[0].vault_user_form_uploads_id,
      tag: JSON.stringify({ document_type: documentTypeValue }),
      ...(isSuggestionTag && { document_suggestion_tag: tag }),
    };
    this.subscription.add(
      this.documentService.createVaultUserTag(payload).subscribe(
        () => {
          this.loadingTags.delete(tag);
          this.attachmentTagsEvent.emit();
        },
        (error: any) => {
          this.loadingTags.delete(tag);
          this.toast.error('Error creating tag', error?.error?.message);
          this._cd.detectChanges();
        }
      )
    );
  }

  deleteVaultUserTag(tag: string) {
    this.subscription.add(
      this.deleteVaultUserTagObservable(tag).subscribe(
        () => {
          this.attachmentTagsEvent.emit();
        },
        (error: any) => {
          this.toast.error('Error deleting tag', error?.error?.message);
        }
      )
    );
  }

  removeDocumentSuggestionTag(tag: string) {
    this.subscription.add(
      this.removeDocumentSuggestionTagObservable(tag).subscribe(
        () => {
          this.attachmentTagsEvent.emit();
        },
        (error: any) => {
          this.toast.error('Error deleting suggestion tag', error?.error?.message);
        }
      )
    );
  }

  deleteVaultUserTagObservable(tag: string): Observable<any> {
    const payload = {
      vault_user_form_uploads_id: [this.files[0].vault_user_form_uploads_id],
      tag: tag,
    };
    return this.documentService.deleteVaultUserTag(payload);
  }

  removeDocumentSuggestionTagObservable(tag: string): Observable<any> {
    const payload = {
      tag_name: tag,
      vaultUserFormId: this.currentFormId,
    };
    return this.documentService.removeInboxDocumentSuggestionTag(payload);
  }

  manageTags() {
    const dialog = this.dialog.open(ManageTagsDialog, {
      data: { ...this.files[0], currentFormId: this.currentFormId, currentFolderId: this.currentFolderId, isEditMode: false },
      autoFocus: false,
      panelClass: 'full-window-dialog-2',
    });
    this.subscription.add(dialog.afterClosed().subscribe((isUpdated) => (isUpdated ? this.attachmentTagsEvent.emit() : '')));
  }

  handleSelectedTag(event: any) {
    if (event?.action) {
      const tagName = event.tag.name;

      if (event.action === 'add') {
        if (this.attachmentTags.document_type.includes(tagName)) {
          return;
        }
        this.createVaultUserTag(tagName);
      } else if (event.action === 'remove') {
        this.deleteVaultUserTag(tagName);
      }
    }
  }

  handleNewCustomTag(tagName: string) {
    this.addUserTags(tagName.trim());
  }

  isTagLoading(tag: string): boolean {
    return this.loadingTags.has(tag);
  }

  onTagSelectionChange(type: 'document_type' | 'tag_ai_suggestion', tag: string, index: number, isChecked: boolean) {
    const key = `${type}_${index}`;
    this.tagSelectionState[key] = isChecked;
    this.tagSelectionChange.emit({ type, tag, index, isChecked });
    this._cd.detectChanges();
  }

  clearSelections() {
    this.tagSelectionState = {};
    this._cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
